import clsx from 'clsx';
import type { AnnotationMode } from '../hooks/useAnnotationMode';
import type { ViewerMode } from '../types';
import { NewButton } from '../../../ui/NewButton/NewButton';
import styles from '../PdfRenderer.module.scss';

interface PdfToolbarProps {
    trailing?: React.ReactNode;
    mode: ViewerMode;
    thumbsOpen: boolean;
    onToggleThumbs: () => void;
    pageNumber: number;
    pageInput: string;
    onPageInputChange: (value: string) => void;
    onPageInputBlur: () => void;
    totalPages: number;
    onPrevPage: () => void;
    onNextPage: () => void;

    zoom: number;
    zoomInput: string;
    onZoomInputChange: (value: string) => void;
    onZoomInputBlur: () => void;
    zoomIn: () => void;
    zoomOut: () => void;
    zoomReset: () => void;
    zoomMin: number;
    zoomMax: number;

    activeMode: AnnotationMode | null;
    activateMode: (mode: AnnotationMode) => void;

    polygonPointsCount: number;
    freehandActive: boolean;
}

export function PdfToolbar({
    trailing,
    mode,
    thumbsOpen,
    onToggleThumbs,
    pageNumber,
    pageInput,
    onPageInputChange,
    onPageInputBlur,
    totalPages,
    onPrevPage,
    onNextPage,
    zoom,
    zoomInput,
    onZoomInputChange,
    onZoomInputBlur,
    zoomIn,
    zoomOut,
    zoomReset,
    zoomMin,
    zoomMax,
    activeMode,
    activateMode,
    polygonPointsCount,
    freehandActive,
}: PdfToolbarProps) {
    const handMode = activeMode === 'hand';
    const moveMode = activeMode === 'move';
    const textMode = activeMode === 'text';
    const placing = activeMode === 'placing';
    const polygonMode = activeMode === 'polygon';
    const freehandMode = activeMode === 'freehand';
    const rectMode = activeMode === 'rect';
    // В review-режиме доступны только read-only инструменты: навигация, зум
    // и «рука» (панорамирование). Всё, что создаёт или двигает геометрию, скрыто.
    const canAnnotate = mode === 'edit';

    // Кнопка инструмента: вид полностью задаётся тулбаром, а не вариантом
    // NewButton — внутри сегментированной группы рамка и белый фон не нужны.
    const toolBtn = (active: boolean) =>
        clsx(styles.toolbarIconBtn, active && styles['toolbarIconBtn--active']);

    return (
        <div className={styles.toolbar}>
            {/* Панель миниатюр. Не режим аннотирования — отдельный boolean,
                а не activateMode, иначе открытие панели гасило бы инструмент. */}
            <div className={styles.toolbarGroup}>
                <NewButton
                    icon="fa-regular fa-images"
                    onClick={onToggleThumbs}
                    className={toolBtn(thumbsOpen)}
                    tooltip="Миниатюры страниц"
                    aria-label="Миниатюры страниц"
                    aria-pressed={thumbsOpen}
                />
            </div>

            {/* Навигация по страницам */}
            <div className={styles.toolbarGroup}>
                <NewButton
                    icon="fa-solid fa-chevron-left"
                    aria-label="Предыдущая страница"
                    onClick={onPrevPage}
                    disabled={pageNumber <= 1}
                    className={toolBtn(false)}
                />
                <div className={styles.pageControl}>
                    <input
                        className={styles.pageInput}
                        value={pageInput !== '' ? pageInput : String(pageNumber)}
                        onChange={(e) => onPageInputChange(e.target.value)}
                        onFocus={(e) => { onPageInputChange(String(pageNumber)); e.target.select(); }}
                        onBlur={onPageInputBlur}
                        onKeyDown={(e) => { if (e.key === 'Enter') { onPageInputBlur(); (e.target as HTMLInputElement).blur(); } }}
                        aria-label="Номер страницы"
                    />
                    <span className={styles.pageTotal}>/ {totalPages || '…'}</span>
                </div>
                <NewButton
                    icon="fa-solid fa-chevron-right"
                    aria-label="Следующая страница"
                    onClick={onNextPage}
                    disabled={pageNumber >= totalPages}
                    className={toolBtn(false)}
                />
            </div>

            {/* Инструменты */}
            <div className={styles.toolbarGroup}>
                {/* Рука — панорамирование */}
                <NewButton
                    icon="fa-solid fa-hand"
                    onClick={() => activateMode('hand')}
                    className={toolBtn(handMode)}
                    tooltip="Рука"
                    aria-label="Режим перемещения"
                />
                {canAnnotate && <>
                {/* Перемещение меток */}
                <NewButton
                    icon="fa-solid fa-up-down-left-right"
                    onClick={() => activateMode('move')}
                    className={toolBtn(moveMode)}
                    tooltip="Перемещение"
                    aria-label="Режим перемещения меток"
                />

                {/* Текстовое замечание */}
                <NewButton
                    icon={textMode ? 'fa-solid fa-xmark' : 'fa-solid fa-highlighter'}
                    onClick={() => activateMode('text')}
                    className={toolBtn(textMode)}
                    tooltip={textMode ? 'Выделите текст…' : 'Аннотация'}
                    aria-label="Аннотация"
                />

                {/* Точечное замечание */}
                <NewButton
                    icon={placing ? 'fa-solid fa-xmark' : 'fa-solid fa-location-dot'}
                    onClick={() => activateMode('placing')}
                    className={toolBtn(placing)}
                    tooltip={placing ? 'Кликните по документу…' : 'Метка'}
                    aria-label="Метка"
                />

                {/* Полигональное замечание */}
                <NewButton
                    icon={polygonMode ? 'fa-solid fa-xmark' : 'fa-solid fa-draw-polygon'}
                    onClick={() => activateMode('polygon')}
                    className={toolBtn(polygonMode)}
                    tooltip={polygonMode ? (polygonPointsCount === 0 ? 'Кликайте по документу…' : `${polygonPointsCount} точек`) : 'Полигон'}
                    aria-label="Полигон"
                />

                {/* Свободный контур */}
                <NewButton
                    icon={freehandMode ? 'fa-solid fa-xmark' : 'fa-solid fa-pencil'}
                    onClick={() => activateMode('freehand')}
                    className={toolBtn(freehandMode)}
                    tooltip={freehandMode ? (freehandActive ? 'Рисуйте…' : 'Кликните для старта') : 'Контур'}
                    aria-label="Контур"
                />

                {/* Прямоугольник */}
                <NewButton
                    icon={rectMode ? 'fa-solid fa-xmark' : 'fa-regular fa-square'}
                    onClick={() => activateMode('rect')}
                    className={toolBtn(rectMode)}
                    tooltip={rectMode ? 'Потяните для рисования…' : 'Прямоугольник'}
                    aria-label="Прямоугольник"
                />
                </>}
            </div>

            {trailing}

            <span className={styles.spacer} />

            {/* Зум */}
            <div className={styles.toolbarGroup}>
                <NewButton
                    icon="fa-solid fa-minus"
                    aria-label="Уменьшить"
                    onClick={zoomOut}
                    disabled={zoom <= zoomMin}
                    className={toolBtn(false)}
                />
                <input
                    className={styles.zoomInput}
                    value={zoomInput !== '' ? zoomInput : `${Math.round(zoom * 100)}%`}
                    onChange={(e) => onZoomInputChange(e.target.value)}
                    onFocus={(e) => { onZoomInputChange(String(Math.round(zoom * 100))); e.target.select(); }}
                    onBlur={onZoomInputBlur}
                    onKeyDown={(e) => { if (e.key === 'Enter') { onZoomInputBlur(); (e.target as HTMLInputElement).blur(); } }}
                    aria-label="Масштаб"
                />
                <NewButton
                    icon="fa-solid fa-plus"
                    aria-label="Увеличить"
                    onClick={zoomIn}
                    disabled={zoom >= zoomMax}
                    className={toolBtn(false)}
                />
                <span className={styles.toolbarDivider} />
                <NewButton
                    onClick={zoomReset}
                    disabled={zoom === 1}
                    className={styles.toolbarTextBtn}
                >
                    Сброс
                </NewButton>
            </div>
        </div>
    );
}
